/**
 * Phase 10 R5 — resolveFinancialVisibility
 *
 * Pure resolver for "can this role see ticket-level financial data
 * (GP card, snapshot, warnings)" given the shop's visibility mode.
 *
 * STRICT BOUNDARIES:
 *   - This is the **display** gate. Editing of shop financial settings is
 *     governed by a separate gate (`ui_labor_pricing` + role).
 *   - Toggling `financial_visibility_mode` does NOT short-circuit this
 *     resolver — it is consulted on every render of every financial surface.
 *   - TECHNICIAN role can never see financial data, in any mode.
 */

export type FinancialVisibilityMode =
  | 'owner_manager_only'
  | 'include_service_writer';

export type FinancialRole = string | null | undefined;

/**
 * Returns true when the given role is permitted to view financial data
 * (GP card, snapshot card, warnings) under the given visibility mode.
 *
 * Order of evaluation:
 *   1. OWNER / MANAGER → always true (mode irrelevant)
 *   2. SERVICE_WRITER → only when mode === 'include_service_writer'
 *   3. anything else (incl. TECHNICIAN, null, unknown) → false
 */
export function canSeeFinancials(
  role: FinancialRole,
  mode: FinancialVisibilityMode | null | undefined,
): boolean {
  if (role === 'OWNER' || role === 'MANAGER') return true;
  if (role === 'SERVICE_WRITER') return mode === 'include_service_writer';
  return false;
}

/**
 * Returns true when the given role is permitted to **edit** shop financial
 * settings (visibility mode + targets + low-margin threshold). This is a
 * **separate** gate from `canSeeFinancials` and intentionally does not
 * consider the visibility mode — only role.
 */
export function canEditFinancialSettings(role: FinancialRole): boolean {
  return role === 'OWNER' || role === 'MANAGER';
}
